/* eslint-disable no-param-reassign */
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { config, socialLogins, FORM_HOOKS_WITH_ICONS } from "../../config";
import {
	getURLQueries,
	setCookie,
	removeCookie,
	validateReturnURL,
} from "../../../../utils/helper";
import { RCL as useTranslation } from "../../../../components/RCL";
import { Icon } from "../../../../components/Icons";
import "../theme/themeIE.scss";

const OktaSignInWidgetIE = ({
	lang,
	isAccountCreated,
	isActionDefault,
	setIsOpen,
	setLoginOpen,
	setUserDismiss,
	isSignIn,
	setSignIn,
	className,
}) => {
	// Injection states
	let isAccountCreatedInjection = false;
	let widgetInit = true;
	let updatedSchema;

	// Add a default consent setting to control the checkbox
	const defaultConsent = false;
	let consent = defaultConsent ? "subscribe" : "unsubscribe";
	let invitePending = false;

	const widgetRef = useRef(null);
	const isAction = isActionDefault || getURLQueries("action");
	const loginHint = getURLQueries("login_hint");
	const getReturnURL = validateReturnURL(getURLQueries("ReturnUrl"), true);
	const userType = getURLQueries("user-type");
	const state = getURLQueries("state");
	const otp = getURLQueries("otp");
	const isInvalidToken =
		getURLQueries("error") === "invalid_token" &&
		(getURLQueries("error_description") === "invalid+or+stale+token+was+provided" ||
			getURLQueries("error_description") === "invalid%20or%20stale%20token%20was%20provided");

	const widgetInstance = useRef();
	const emailFound = useRef(false);

	const cleanQueryURL = _queries => {
		const siteUrl = new URL(window.location.href);
		const queries = siteUrl.searchParams;

		_queries.forEach(query => {
			queries.set(query, "");
		});

		siteUrl.search = queries.toString();

		const obj = { Title: document.title, Url: siteUrl.toString() };
		window.history.pushState(obj, obj.Title, obj.Url);
	};

	const processHelpLinks = linksCopy => {
		if (typeof linksCopy !== "string") {
			return null;
		}
		const textLinks = linksCopy.trim().replace(/\n/g, "").split(",");
		const linksArray = [];

		textLinks.forEach(textLink => {
			const textArray = textLink.trim().split(";");

			const linkObject = {
				text: "",
				href: "",
				target: null,
			};

			let linkFlag = false;

			for (let i = 0; i < textArray.length; i++) {
				// Sanitize array
				const tempString = textArray[i].trim();

				if (tempString.length) {
					textArray[i] = tempString;
				} else {
					textArray[i] = null;
				}

				// Build output array
				if (textArray[i]) {
					// eslint-disable-next-line no-loop-func
					Object.keys(linkObject).forEach(key => {
						const textItem = textArray[i].split(":");

						if (key === textItem[0]) {
							switch (key) {
								case "text":
									// eslint-disable-next-line prefer-destructuring
									linkObject[key] = textItem[1];
									linkFlag = true;
									break;
								case "href":
									// eslint-disable-next-line no-case-declarations
									const href = textArray[i].substr(key.length + 1, textArray[i].length - 1);
									linkObject[key] = href;
									linkFlag = true;
									break;
								case "target":
									// eslint-disable-next-line prefer-destructuring
									linkObject[key] = textItem[1];
									linkFlag = true;
									break;
								default:
									linkFlag = false;
							}
						}
					});
				}
			}

			// Update output array
			if (linkFlag && linkObject.text && linkObject.href) {
				linksArray.push(linkObject);
			}
		});

		if (linksArray) {
			return linksArray;
		}
		return null;
	};

	const dictionary = {
		"okta-widget-sign-in-with-facebook": useTranslation({
			searchKey: "okta-widget-sign-in-with-facebook",
		}),
		"okta-widget-sign-in-with-google": useTranslation({
			searchKey: "okta-widget-sign-in-with-google",
		}),
		"okta-widget-sign-in-with-linkedin": useTranslation({
			searchKey: "okta-widget-sign-in-with-linkedin",
		}),
		"okta-widget-sign-in-with-apple": useTranslation({
			searchKey: "okta-widget-sign-in-with-apple",
		}),
		"okta-widget-email-placeholder": useTranslation({
			searchKey: "okta-widget-email-placeholder",
		}),
		"okta-widget-password": useTranslation({
			searchKey: "okta-widget-password",
		}),
		"okta-widget-unlock-account": useTranslation({
			searchKey: "okta-widget-unlock-account",
		}),
		"okta-sign-in-error-expired-swg": useTranslation({
			searchKey: "okta-sign-in-error-expired-swg",
		}),
		"okta-widget-error-E0000004": useTranslation({
			searchKey: "okta-widget-error-E0000004",
		}),
		"okta-widget-error-E0000006": useTranslation({
			searchKey: "okta-widget-error-E0000006",
		}),
		"okta-widget-error-E0000119": useTranslation({
			searchKey: "okta-widget-error-E0000119",
		}),
		"okta-widget-consent-label": useTranslation({
			searchKey: "okta-widget-consent-label",
		}),
		"okta-widget-social-text": useTranslation({
			searchKey: "okta-widget-social-text",
		}),
		"okta-widget-sign-up-text": useTranslation({
			searchKey: "okta-widget-sign-up-text",
		}),
		"okta-widget-create-an-account": useTranslation({
			searchKey: "okta-widget-create-an-account",
		}),
		"okta-widget-new-account-heading": useTranslation({
			searchKey: "okta-widget-new-account-heading",
		}),
		"okta-widget-new-account-message": useTranslation({
			searchKey: "okta-widget-new-account-message",
		}),
		"modal-close": useTranslation({
			searchKey: "modal-close",
		}),
		"okta-widget-goback-text": useTranslation({
			searchKey: "okta-widget-goback-text",
		}),
		"okta-profile-panel-heading-membership": useTranslation({
			searchKey: "okta-profile-panel-heading-membership",
		}),
		"okta-widget-sign-in-title": useTranslation({
			searchKey: "okta-widget-sign-in-title",
		}),
		"okta-widget-social-text-v2": useTranslation({
			searchKey: "okta-widget-social-text-v2",
		}),
		"okta-widget-legal": useTranslation({ searchKey: "okta-widget-legal" }),
		"okta-helplinks": useTranslation({
			searchKey: "okta-helplinks-custom",
		}),
		"okta-widget-verification-email-message": useTranslation({
			searchKey: "okta-widget-verification-email-message",
		}),
		"okta-widget-verify-email-instructions": useTranslation({
			searchKey: "okta-widget-verify-email-instructions",
		}),
		"okta-widget-verify-email-instructions-magic-link": useTranslation({
			searchKey: "okta-widget-verify-email-instructions-magic-link",
		}),
		"okta-widget-verify-email-instructions-alt-magic-link": useTranslation({
			searchKey: "okta-widget-verify-email-instructions-alt-magic-link",
		}),
		"okta-widget-label-username": useTranslation({
			searchKey: "okta-widget-label-username",
		}),
	};

	const customHelpLinks = processHelpLinks(dictionary["okta-helplinks"]);

	// Build IDP array
	const socialLoginArray = socialLogins.map(idp => ({
		...idp,
		text: " ",
	}));

	const injectConsent = _ref => {
		const consentElement = _ref.querySelector(".custom-checkbox");

		if (consentElement) {
			return;
		}

		/* Injects Consent checkbox and label into the widget in order to simplify the registration process */
		const consentContainerElement = _ref.querySelector(".o-form-fieldset-container");

		const consentWrapperElement = document.createElement("div");
		consentWrapperElement.className = "o-form-fieldset o-form-label-top margin-btm-0";

		const inputWrapperElement = document.createElement("div");
		inputWrapperElement.className = "custom-checkbox";

		const inputElement = document.createElement("input");
		inputElement.type = "checkbox";
		inputElement.checked = consent === "subscribe";
		inputElement.id = "subscribe";

		inputWrapperElement.appendChild(inputElement);

		const inputLabelElement = document.createElement("label");
		inputLabelElement.htmlFor = "subscribe";
		inputLabelElement.innerText = dictionary["okta-widget-consent-label"];
		inputLabelElement.className = consent === "subscribe" ? "checked" : "";

		inputWrapperElement.appendChild(inputLabelElement);

		consentWrapperElement.appendChild(inputWrapperElement);

		consentContainerElement.appendChild(consentWrapperElement);

		// Update UI and profile state
		inputElement.addEventListener("change", e => {
			if (e.target.checked) {
				consent = "subscribe";
				inputLabelElement.classList.add("checked");
			} else {
				consent = "unsubscribe";
				inputLabelElement.classList.remove("checked");
			}
		});

		inputElement.addEventListener("focus", () => {
			inputLabelElement.classList.add("hover");
		});

		inputElement.addEventListener("focusout", () => {
			inputLabelElement.classList.remove("hover");
		});

		inputLabelElement.addEventListener("mouseover", () => {
			inputLabelElement.classList.add("hover");
		});

		inputLabelElement.addEventListener("mouseout", () => {
			inputLabelElement.classList.remove("hover");
		});
	};

	const injectLegal = (_ref, _add = false) => {
		// Form element target for legal
		const contentDIV = _ref.getElementsByClassName("auth-content")[0];

		// Check if legal copy exists in widget
		const legalElementDIV = _ref.querySelector(".legal");
		if (!legalElementDIV && _add) {
			// Inject legal copy into widget
			const legalDIV = document.createElement("div");
			legalDIV.innerHTML = dictionary["okta-widget-legal"];
			legalDIV.className = "legal";

			contentDIV.appendChild(legalDIV);
		} else if (!_add) {
			// Remove all legal
			const legalDIV = _ref.querySelectorAll(".legal");

			if (legalDIV.length > 0) {
				legalDIV.forEach(item => {
					item.parentNode.removeChild(item);
				});
			}
		}
	};

	const injectAffiliateField = (_ref, _view, _margin = false) => {
		const afElement = _ref.querySelector(".affiliate-program");

		if (afElement) {
			return;
		}

		const affiliateProgram = sessionStorage?.getItem("affiliateProgram");
		const containerElement = document.querySelector(`${_view} .o-form-fieldset-container`);

		if (affiliateProgram && containerElement) {
			const wrapperElement = document.createElement("div");
			wrapperElement.className = `affiliate-program o-form-fieldset o-form-label-top${
				_margin ? "" : " margin-btm-0"
			}`;

			const labelElement = document.createElement("div");
			labelElement.className = "okta-form-label o-form-label";

			const inputLabelElement = document.createElement("label");
			inputLabelElement.htmlFor = "affiliate-program";
			inputLabelElement.innerText = dictionary["okta-profile-panel-heading-membership"];

			labelElement.appendChild(inputLabelElement);
			wrapperElement.appendChild(labelElement);

			const inputContainerElement = document.createElement("div");
			inputContainerElement.className = "o-form-input";

			const inputWrapperElement = document.createElement("span");
			inputWrapperElement.className =
				"o-form-input-name-affiliate o-form-control okta-form-input-field input-fix";

			const inputElement = document.createElement("input");
			inputElement.type = "text";
			inputElement.value = affiliateProgram;
			inputElement.id = "affiliate-program";
			inputElement.name = "affiliate-program";
			inputElement.readOnly = true;

			inputWrapperElement.appendChild(inputElement);
			inputContainerElement.appendChild(inputWrapperElement);
			wrapperElement.appendChild(inputContainerElement);
			containerElement.insertAdjacentElement("afterbegin", wrapperElement);
		}
	};

	const injectAccountCreated = (_ref, _injectState, _displayIcon) => {
		const oktaHeader = _ref.getElementsByClassName("okta-sign-in-header")[0];

		if (isAccountCreated && !isAccountCreatedInjection && _injectState === "add") {
			if (oktaHeader) {
				// Inject account creation status into widget
				const activateElement = document.createElement("div");
				activateElement.className = "account-activation";

				const headingElement = document.createElement("span");
				headingElement.className = "heading";

				const headingIcon = document.createElement("span");
				headingIcon.className = "swg-icon swg-icon-checked--o";
				headingElement.appendChild(headingIcon);

				const headingText = document.createElement("span");
				headingText.className = "status";
				headingText.innerText = dictionary["okta-widget-new-account-heading"];
				headingElement.appendChild(headingText);

				activateElement.appendChild(headingElement);

				const messageElement = document.createElement("p");
				messageElement.className = "message";
				messageElement.innerText = dictionary["okta-widget-new-account-message"];
				activateElement.appendChild(messageElement);

				oktaHeader.appendChild(activateElement);
				oktaHeader.classList.add("activate");

				oktaHeader.classList.remove("icon");

				// Modify registration query strings so if the user bookmarks, the success message will not display again
				cleanQueryURL(["type_hint", "session_hint"]);
				isAccountCreatedInjection = true;
			}
		} else {
			const activateElement = _ref.getElementsByClassName("account-activation")[0];

			if (activateElement && isAccountCreatedInjection) {
				activateElement.parentNode.removeChild(activateElement);
				oktaHeader.classList.remove("activate");

				isAccountCreatedInjection = false;
			}

			if (_displayIcon) {
				oktaHeader.classList.add("icon");
			} else {
				oktaHeader.classList.remove("icon");
			}
		}
	};

	const injectErrorAlert = (_ref, _injectState, _message) => {
		const oktaErrorContainerEl = _ref.querySelector(".o-form-error-container");

		if (oktaErrorContainerEl) {
			if (_injectState === "add") {
				const errorEl = document.createElement("div");
				errorEl.className = "okta-form-infobox-error infobox infobox-error";
				errorEl.role = "alert";

				const errorIconEl = document.createElement("span");
				errorIconEl.className = "icon error-16";

				errorEl.appendChild(errorIconEl);

				const messageElement = document.createElement("p");
				messageElement.innerText = _message;
				errorEl.appendChild(messageElement);

				oktaErrorContainerEl.innerHTML = null;
				oktaErrorContainerEl.appendChild(errorEl);
				oktaErrorContainerEl.classList.add("o-form-has-errors");
			} else {
				oktaErrorContainerEl.classList.remove("o-form-has-errors");
				oktaErrorContainerEl.innerHTML = null;
			}
		}
	};

	const updateSocialButtons = instance => {
		// Social media buttons
		const socialMediaButtons = instance.querySelectorAll(".social-auth-button.link-button");

		if (socialMediaButtons) {
			// Remove text and add accessibility label
			let buttonClasses = "";
			let idp = "";

			socialMediaButtons.forEach(button => {
				let isFound = false;
				buttonClasses = button.className;

				socialLoginArray.forEach(idpButton => {
					idp = idpButton.type.toLowerCase();

					if (!isFound && buttonClasses) {
						if (buttonClasses?.indexOf(idp) > -1) {
							button.ariaLabel = dictionary[`okta-widget-sign-in-with-${idp}`];
							button.innerText = "";
							isFound = true;
						} else {
							button.ariaLabel = "";
						}
					}
				});
			});
		}
	};

	const handleInit = _ref => {
		widgetInit = false;
		const signUpElement = _ref.getElementsByClassName("js-enroll")[0];
		const forgotElement = _ref.getElementsByClassName("js-forgot-password")[0];
		const unlockElement = _ref.getElementsByClassName("js-unlock")[0];

		// Detection for account actions and defaults to screen based on querystring
		if (isAction === "register" && signUpElement) {
			signUpElement.click();
		} else if (isAction === "forgot" && forgotElement) {
			forgotElement.click();
		} else if (isAction === "unlock") {
			unlockElement.click();
		} else if (loginHint) {
			const updateHint = usernameElement => {
				if (usernameElement) {
					usernameElement.value = decodeURIComponent(loginHint);
					usernameElement.dispatchEvent(new Event("change", { bubbles: true }));
				}
			};

			// Auto fill the email when new account creation is sucessful
			const usernameSignIn = _ref.querySelector("[name='identifier']");

			if (usernameSignIn) {
				updateHint(usernameSignIn);

				const nextButton = _ref.querySelector(".button-primary");

				if (nextButton) {
					setTimeout(() => {
						nextButton.focus();
					}, 200);
				}
			}
		}

		if (isAction) {
			// Modify action query string so if user cancels flow it will not default again
			cleanQueryURL(["action"]);
		}

		if (isInvalidToken) {
			injectErrorAlert(_ref, "add", dictionary["okta-sign-in-error-expired-swg"]);
		}

		// Set cookie expiry
		const expiration = new Date();
		expiration.setHours(expiration.getHours() + 2);

		// Store Return URL
		if (getReturnURL !== null) {
			// Redirect process started by MBP website
			setCookie("sso-destination", getReturnURL, "/", window.location.hostname, expiration);
		} else if (getURLQueries("ReturnUrl")) {
			removeCookie("sso-destination", "/", window.location.hostname);
		}

		// Setup user type tracking
		if (userType) {
			setCookie("sso-type", userType, "/", window.location.hostname, expiration);
		}

		// Sets the Okta environment language
		const oktaLangExpiration = new Date();
		oktaLangExpiration.setUTCMonth(oktaLangExpiration.getMonth() + 1);
		setCookie("okta_user_lang", lang.toLowerCase(), "/", ".sunwing.ca", oktaLangExpiration);
	};

	const removeAttributeNodes = _ref => {
		updatedSchema.forEach(schema => {
			if (schema?.remove) {
				const schemaElement = _ref.querySelector(`[data-se='${schema["data-se"]}']`);
				schemaElement.parentNode.removeChild(schemaElement);
			}
		});
	};

	const moveRegistration = _ref => {
		// Move Sign-up container
		const authContainer = _ref?.getElementsByClassName("sign-in-with-idp")[0];
		const signUpContainer = _ref?.getElementsByClassName("footer-info")[0];
		if (signUpContainer) {
			signUpContainer.classList.add("credential-su");
			if (authContainer) authContainer.insertAdjacentElement("beforebegin", signUpContainer);
		}
	};

	const removeHelpLink = _ref => {
		// Remove help link
		const helpElement = _ref.querySelector(".js-help");
		if (helpElement) {
			helpElement.parentNode.removeChild(helpElement);
		}
	};

	const removeAuthenticatorListLink = _ref => {
		// Remove "Return to authenticator list" link
		const authListLinkElement = _ref.querySelector(".js-switchAuthenticator");
		if (authListLinkElement) {
			authListLinkElement.parentNode.removeChild(authListLinkElement);
		}
	};

	const injectUserEmail = _ref => {
		const userEmail = _ref.querySelector("[data-se='identifier']");
		const displayMessage = _ref.querySelector("[data-se='o-form-explain']");
		const boldEmail = _ref.querySelector("[data-se='o-form-explain'] .no-translate");

		if (boldEmail?.innerText?.length) {
			boldEmail.innerText = userEmail.innerText;
			emailFound.current = true;
		} else if (displayMessage?.innerText?.length) {
			if (displayMessage.innerText.indexOf("{email}") >= 0) {
				emailFound.current = true;
			}

			displayMessage.innerHTML = displayMessage.innerText.replace(
				"{email}",
				`<span class="strong">${userEmail.innerText}</span>`
			);
		}
	};

	const selectAuthenticationOption = (_ref, _id) => {
		// Auto select authentication option from list
		const authList = _ref.querySelectorAll(".authenticator-list .authenticator-row");
		if (authList) {
			let match = false;
			authList.forEach(element => {
				const button = element.querySelector(`[data-se='${_id}'] [data-se='button']`);
				if (button && !match) {
					match = true;
					button.click();
				}
			});
		}
	};

	const updateClasses = (_ref, action, classArray, selector) => {
		const identifierContainer = _ref.querySelector(selector);

		if (identifierContainer) {
			if (action && classArray) {
				identifierContainer.classList[action](classArray.join(" "));
			}
		}
	};

	const renameUnlockLabel = _ref => {
		const labelName = dictionary["okta-widget-label-username"];
		const unlockFieldLabel = _ref.querySelectorAll("[data-se='o-form-label'] > label");

		if (unlockFieldLabel?.[0]) {
			unlockFieldLabel[0].innerText = labelName;
		}
	};

	const widgetConfig = {
		...config,
		useClassicEngine: false,
		useInteractionCodeFlow: true,
		baseUrl: config.domain,
		redirectUri: `${window.location.origin}/${lang}/login/callback`,
		authParams: {
			issuer: config.issuer,
			scopes: config.scopes,
			pkce: config.pkce,
		},
		language: lang,
		features: {
			registration: true,
			rememberMe: true,
			selfServiceUnlock: true,
		},
		state,
		otp,
		hooks: {
			identify: {
				// Primary screen
				after: [
					async function afterSigninRedirect() {
						// Home screen for login flow
						setTimeout(() => {
							moveRegistration(widgetRef.current);
							removeHelpLink(widgetRef.current);
							updateSocialButtons(widgetRef.current);
							injectAffiliateField(widgetRef.current, ".siw-main-view");
							injectAccountCreated(widgetRef.current, "add");
							injectLegal(widgetRef.current);
							consent = defaultConsent ? "subscribe" : "unsubscribe";
							setSignIn(true);

							if (widgetInit) {
								handleInit(widgetRef.current);
							}
						}, 1);
					},
				],
			},
			"identify-recovery": {
				after: [
					async function afterForgotRedirect() {
						// Forgot password screen

						setSignIn(false);
					},
				],
			},
			"enroll-profile": {
				after: [
					async function afterEnrollProfileRedirect() {
						// Enrollment / create account screen

						setTimeout(() => {
							injectConsent(widgetRef.current);
							injectLegal(widgetRef.current, true);
							injectAffiliateField(widgetRef.current, ".siw-main-view");
							injectAccountCreated(widgetRef.current, "remove");
							setSignIn(false);

							if (updatedSchema) {
								removeAttributeNodes(widgetRef.current);
							}
						}, 1);
					},
				],
			},
			"select-authenticator-enroll": {
				after: [
					async function afterAuthenticationSelectEnrollRedirect() {
						// Enrollment / create account confirmation

						setTimeout(() => {
							injectAccountCreated(widgetRef.current, "remove");
							injectLegal(widgetRef.current);
							setSignIn(false);
						}, 1);
					},
				],
			},
			"select-authenticator-enroll-data": {
				after: [
					async function afterAuthenticationSelectEnrollDataRedirect() {
						// Enrollment / create account confirmation

						setTimeout(() => {
							injectAccountCreated(widgetRef.current, "remove");
							setSignIn(false);
						}, 1);
					},
				],
			},
			"unlock-account": {
				after: [
					async function afterUnlockAccounteRedirect() {
						// Unlock account request screen

						setTimeout(() => {
							renameUnlockLabel(widgetRef.current);
							injectAccountCreated(widgetRef.current, "remove");
							setSignIn(false);
						}, 1);
					},
				],
			},
			"select-authenticator-unlock-account": {
				after: [
					async function afterUnlockRedirect() {
						// Unlock account email entry screen

						setTimeout(() => {
							selectAuthenticationOption(widgetRef.current, "okta_email");
							injectAccountCreated(widgetRef.current, "remove");
							setSignIn(false);
						}, 1);
					},
				],
			},
			"authenticator-verification-data": {
				after: [
					async function afterVerifyEmailRedirect() {
						// Account verify email screen

						setTimeout(() => {
							injectAccountCreated(widgetRef.current, "remove", true);
							setSignIn(false);
						}, 1);
					},
				],
			},
			"challenge-authenticator": {
				after: [
					async function afterChallengeRedirect() {
						// Verification challenge screen

						setTimeout(() => {
							injectAccountCreated(widgetRef.current, "remove", true);
							setSignIn(false);
						}, 1);
					},
				],
			},
			"enroll-authenticator": {
				after: [
					async function afterEnrollAuthenticateRedirect() {
						// Enrollment authentication request screen

						setTimeout(() => {
							injectAccountCreated(widgetRef.current, "remove", true);
							removeAuthenticatorListLink(widgetRef.current);
							setSignIn(false);
						}, 1);
					},
				],
			},
		},
		helpLinks: {
			custom: customHelpLinks,
		},
		i18n: {
			[lang]: {
				// COMMON LABELS
				"primaryauth.title": dictionary["okta-widget-sign-in-title"],
				"primaryauth.username.placeholder": dictionary["okta-widget-email-placeholder"],
				unlockaccount: dictionary["okta-widget-unlock-account"],
				"socialauth.divider.text": dictionary["okta-widget-social-text-v2"],
				goback: dictionary["okta-widget-goback-text"],

				// ERRORS
				"errors.E0000004": dictionary["okta-widget-error-E0000004"],
				"errors.E0000006": dictionary["okta-widget-error-E0000006"],
				"errors.E0000119": dictionary["okta-widget-error-E0000119"],

				// OIE
				"oie.registration.form.title": dictionary["okta-widget-create-an-account"],
				"oie.registration.form.submit": dictionary["okta-widget-create-an-account"],
				"oie.user.profile.primary.email": dictionary["okta-widget-email-placeholder"],
				"oie.email.label": dictionary["okta-widget-email-placeholder"],
				"oie.user.profile.login": dictionary["okta-widget-label-username"],
				"oie.email.verify.alternate.instructions":
					dictionary["okta-widget-verify-email-instructions"],
				"oie.email.verify.alternate.magicLinkToYourEmail":
					dictionary["okta-widget-verify-email-instructions-alt-magic-link"],
				"oie.email.verify.alternate.magicLinkToEmailAddress":
					dictionary["okta-widget-verify-email-instructions-magic-link"],
				"oie.email.verify.subtitle.text.without.email":
					dictionary["okta-widget-verification-email-message"],
				haveaccount: dictionary["okta-widget-goback-text"],
			},
		},

		idps: socialLoginArray,
		recoveryToken: getURLQueries("token"),
		registration: {
			parseSchema: (schema, onSuccess) => {
				/* TODO: Move to Netlify */
				// Manage hidden attributes
				const attributeArray = [
					"subscribe",
					"subscribeUpdateDate",
					"affiliateInviteCode",
					"locale",
					"preferredLanguage",
				];

				attributeArray.forEach(attribute => {
					const attributeConfig = schema.filter(s => s.name === `userProfile.${attribute}`);

					if (attributeConfig?.[0]) {
						attributeConfig[0].remove = true;
					}
				});

				updatedSchema = schema;
				onSuccess(schema);
			},
			preSubmit: (postData, onSuccess) => {
				postData.userProfile.preferredLanguage = lang;
				postData.userProfile.locale = lang.concat("_CA");

				const affiliateCode = sessionStorage.getItem("affiliateCode");
				invitePending = sessionStorage.getItem("invitePending") === "true";

				if (affiliateCode && invitePending) {
					// eslint-disable-next-line prefer-destructuring
					postData.userProfile.affiliateInviteCode = affiliateCode.split("|")[0];
					localStorage.setItem("affiliateRegistration", true);
				}

				// Set value based on UI state
				if (consent === "subscribe") {
					postData.userProfile.subscribe = consent;
				} else {
					postData.userProfile.subscribe = "";
				}

				onSuccess(postData);
			},
			postSubmit: (response, onSuccess) => {
				if (invitePending) {
					invitePending = false;
					sessionStorage.removeItem("invitePending");
					sessionStorage.removeItem("affiliateCode");
					sessionStorage.removeItem("affiliateProgram");
				}
				onSuccess(response);
			},
		},
	};

	useEffect(() => {
		if (!widgetRef.current) return false;

		import("@okta/okta-signin-widget").then(({ default: OktaSignIn }) => {
			if (widgetRef.current) {
				localStorage.removeItem("okta-cache-storage");
				widgetInstance.current = new OktaSignIn(widgetConfig);
				widgetInstance.current.showSignInAndRedirect({ el: widgetRef.current });

				widgetInstance.current.on("afterError", (_context, _error) => {
					if (_error?.xhr?.responseJSON?.errorSummaryKeys?.includes("idx.session.expired")) {
						if (FORM_HOOKS_WITH_ICONS.includes(_context.formName)) {
							// Show icon
							injectAccountCreated(widgetRef.current, "remove", true);
						} else {
							// Hide icon
							injectAccountCreated(widgetRef.current, "remove", false);
						}
					} else if (_error?.xhr?.responseJSON?.errorCauses.length) {
						const errorArray = _error.xhr.responseJSON.errorCauses;

						errorArray.forEach(errorItem => {
							if (errorItem.errorKey.includes("api.authn.error.PASSCODE_INVALID")) {
								updateClasses(widgetRef.current, "remove", ["hide"], ".identifier-container");
							}
						});
					}
				});

				widgetInstance.current.on("afterRender", _context => {
					if (_context.formName === "terminal" || _context.formName === "reset-authenticator") {
						injectAccountCreated(widgetRef.current, "remove", true);
					} else if (
						(_context.formName === "challenge-authenticator" &&
							_context.controller === "mfa-verify-passcode") ||
						(_context.controller === "enroll-email" &&
							_context.formName === "enroll-authenticator") ||
						_context.formName === "authenticator-verification-data"
					) {
						setTimeout(() => {
							injectUserEmail(widgetRef.current);
							const identifierContainer = widgetRef.current.querySelector(".identifier-container");

							if (emailFound.current && identifierContainer) {
								identifierContainer.classList.add("hide");
								// Reset flag
								emailFound.current = false;
							}
						}, 10);

						const codeLink = widgetRef.current.querySelector(".enter-auth-code-instead-link");
						if (codeLink) {
							// Auto click "Enter a verification code"
							codeLink.click();
						}
					} else if (
						(_context.controller === "enroll-choices" &&
							_context.formName === "select-authenticator-enroll") ||
						_context.formName === "select-authenticator-unlock-account"
					) {
						selectAuthenticationOption(widgetRef.current, "okta_email");
						updateClasses(widgetRef.current, "add", ["hide"], ".identifier-container");
					}
				});
			}
		});

		return () => {
			if (widgetInstance.current) {
				widgetInstance.current.remove();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{(isAccountCreated || isSignIn) && (
				<div className="close">
					<div className="closeMobile">
						<button
							type="button"
							aria-label={dictionary["modal-close"]}
							onClick={() => {
								setIsOpen(false);
								setLoginOpen(false);
								setUserDismiss(true);
							}}>
							<Icon name="close--n" aria-hidden="true" />
						</button>
					</div>
				</div>
			)}
			<div className={`sso-widget ${className}`} ref={widgetRef} />
		</>
	);
};

OktaSignInWidgetIE.propTypes = {
	lang: PropTypes.oneOf(["en", "fr"]),
	isAccountCreated: PropTypes.bool.isRequired,
	isActionDefault: PropTypes.string.isRequired,
	setIsOpen: PropTypes.func.isRequired,
	setLoginOpen: PropTypes.func.isRequired,
	setUserDismiss: PropTypes.func.isRequired,
	isSignIn: PropTypes.bool.isRequired,
	setSignIn: PropTypes.func.isRequired,
	className: PropTypes.string.isRequired,
};

OktaSignInWidgetIE.defaultProps = {
	lang: "en",
};

export default OktaSignInWidgetIE;
